/**
 * interface for chest items
 * id: the id of the item
 * val: the amount of the item rewarded
 */
import { IRewardItem } from '../State/reducers/score';
import { ItemIDs } from './Items';

/**
 * interface for the mapchest object
 * index: the position of the chest in the unlockedChests array in PlayerState
 * levelNum: the level where the chest will be unlocked
 * size: the visual size of the chest
 * contend: the reward inside the chest
 */
export interface IMapChests {
    index: number;
    levelNum: number;
    position?: [number, number];
    size: ChestSize;
    content: IRewardItem[];
}

interface IAllMapChests {
    [key: string]: IMapChests[];
}

/**
 * the different possible chest sizes
 */
export enum ChestSize {
    SMALL = 'small',
    REGULAR = 'regular',
    BIG = 'big',
}

/**
 * the different states a chest can be in
 * LOCKED: the chest is locked and cant be opened
 * UNLOCKED: the chest is ready to be opened
 * OPENED: the chest has been opened
 */
export enum MapChestsStates {
    LOCKED = 'locked',
    UNLOCKED = 'unlocked',
    OPENED = 'opened',
}

/**
 * all the data for the mapchests in the game
 * called for each map by the map id
 */
export const AllMapChests: IAllMapChests = {
    machu: [
        {
            index: 0,
            levelNum: 10,
            size: ChestSize.SMALL,
            position: [1226.68, 6329.5],
            content: [
                {
                    id: ItemIDs.COINS,
                    val: 150,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 1,
            levelNum: 20,
            size: ChestSize.REGULAR,
            position: [739, 4842.5],
            content: [
                {
                    id: ItemIDs.MAGNET,
                    val: 1,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 2,
            levelNum: 35,
            size: ChestSize.REGULAR,
            position: [1218.72, 2449.5],
            content: [
                {
                    id: ItemIDs.FIRECRACKER,
                    val: 1,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 3,
            levelNum: 49,
            size: ChestSize.BIG,
            position: [626.76, 486.5],
            content: [
                {
                    id: ItemIDs.COINS,
                    val: 500,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 20,
                },
            ],
        },
    ],
    rome: [
        {
            index: 4,
            levelNum: 56,
            size: ChestSize.SMALL,
            position: [1164.68, 6259.5],
            content: [
                {
                    id: ItemIDs.FIRECRACKER,
                    val: 1,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 5,
            levelNum: 70,
            size: ChestSize.SMALL,
            position: [1128, 4488.5],
            content: [
                {
                    id: ItemIDs.COINS,
                    val: 250,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 6,
            levelNum: 85,
            size: ChestSize.REGULAR,
            position: [1218.72, 2449.5],
            content: [
                {
                    id: ItemIDs.MAGNET,
                    val: 1,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 7,
            levelNum: 99,
            size: ChestSize.BIG,
            position: [626.76, 486.5],
            content: [
                {
                    id: ItemIDs.COINS,
                    val: 500,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 20,
                },
            ],
        },
    ],
    seoul: [
        {
            index: 8,
            levelNum: 107,
            size: ChestSize.SMALL,
            position: [1127.68, 6163.5],
            content: [
                {
                    id: ItemIDs.MAGNET,
                    val: 1,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 9,
            levelNum: 119,
            size: ChestSize.SMALL,
            position: [848, 4811.5],
            content: [
                {
                    id: ItemIDs.COINS,
                    val: 250,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 10,
            levelNum: 136,
            size: ChestSize.REGULAR,
            position: [1022.72, 2323.5],
            content: [
                {
                    id: ItemIDs.FIRECRACKER,
                    val: 1,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 11,
            levelNum: 149,
            size: ChestSize.BIG,
            position: [626.76, 486.5],
            content: [
                {
                    id: ItemIDs.COINS,
                    val: 500,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 20,
                },
            ],
        },
    ],
    scotland: [
        {
            index: 12,
            levelNum: 157,
            size: ChestSize.SMALL,
            position: [1065.68, 6269.5],
            content: [
                {
                    id: ItemIDs.MAGNET,
                    val: 1,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 13,
            levelNum: 169,
            size: ChestSize.SMALL,
            position: [848, 4749.5],
            content: [
                {
                    id: ItemIDs.COINS,
                    val: 250,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 14,
            levelNum: 188,
            size: ChestSize.REGULAR,
            position: [877.72, 2058.5],
            content: [
                {
                    id: ItemIDs.FIRECRACKER,
                    val: 1,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 15,
            levelNum: 199,
            size: ChestSize.BIG,
            position: [626.76, 486.5],
            content: [
                {
                    id: ItemIDs.COINS,
                    val: 500,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 20,
                },
            ],
        },
    ],
    morocco: [
        {
            index: 16,
            levelNum: 207,
            size: ChestSize.SMALL,
            position: [1141.68, 6269.5],
            content: [
                {
                    id: ItemIDs.MAGNET,
                    val: 1,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 17,
            levelNum: 219,
            size: ChestSize.SMALL,
            position: [848, 4749.5],
            content: [
                {
                    id: ItemIDs.COINS,
                    val: 250,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 18,
            levelNum: 238,
            size: ChestSize.REGULAR,
            position: [877.72, 2058.5],
            content: [
                {
                    id: ItemIDs.FIRECRACKER,
                    val: 1,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 19,
            levelNum: 249,
            size: ChestSize.BIG,
            position: [626.76, 486.5],
            content: [
                {
                    id: ItemIDs.COINS,
                    val: 500,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 20,
                },
            ],
        },
    ],
    newyork: [
        {
            index: 20,
            levelNum: 257,
            size: ChestSize.SMALL,
            position: [1065.68, 6269.5],
            content: [
                {
                    id: ItemIDs.MAGNET,
                    val: 1,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 21,
            levelNum: 269,
            size: ChestSize.SMALL,
            position: [1279, 4696],
            content: [
                {
                    id: ItemIDs.COINS,
                    val: 250,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 22,
            levelNum: 288,
            size: ChestSize.REGULAR,
            position: [877.72, 2196.5],
            content: [
                {
                    id: ItemIDs.FIRECRACKER,
                    val: 1,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 10,
                },
            ],
        },
        {
            index: 23,
            levelNum: 299,
            size: ChestSize.BIG,
            position: [626.76, 486.5],
            content: [
                {
                    id: ItemIDs.COINS,
                    val: 500,
                },
                {
                    id: ItemIDs.COLLECTABLE_TOKEN,
                    val: 20,
                },
            ],
        },
    ],
};
